'use client';

/**
 * Carousel Banner Component
 *
 * Hero carousel with auto-play and swipe gestures.
 * Displays promotional slides and CTAs.
 * Supports image-based slides with optional text overlays.
 *
 * NO framer-motion: this component renders the LCP element on the homepage
 * and all 17 landers, and it was the last framer-motion dependency on the
 * marketing critical path (~116KB executing before first paint). Slide
 * transitions are CSS keyframes (two absolutely-positioned layers), swipe is
 * native touch events. Slide 0 renders statically in SSR HTML — no
 * animation class until the first transition, so first paint is untouched.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

import { ChevronLeft, ChevronRight } from 'lucide-react';

import { useUser } from '@kit/supabase/hooks/use-user';

import { useAuthModal } from '~/components/auth/auth-modal-context';

// Constants
const AUTO_PLAY_INTERVAL = 6000;
const ANIMATION_MS = 500;
/** Minimum horizontal swipe distance (px) to change slide. */
const SWIPE_DISTANCE_PX = 48;

interface BannerSlide {
  id: number;
  type: 'hero' | 'promo' | 'image';
  title: string;
  subtitle: string;
  buttonText: string;
  buttonLink: string;
  gradient: string;
  tagline?: string;
  image?: string; // Path to banner image (used on desktop when mobileImage is set)
  // Optional phone-only image. When set, this is shown below the `sm` breakpoint
  // and `image` is shown at/above it — lets a 2:1 phone asset and a wide desktop
  // asset coexist on the same slide without cropping either.
  mobileImage?: string;
  imageAlt?: string; // Alt text for accessibility
  action?: 'link' | 'auth-modal'; // Default is 'link'
  // Optional override for the <Image> classes. Defaults to
  // 'object-cover object-top'. Use to fix per-slide framing without
  // touching the source asset (e.g. 'object-contain' to avoid cropping).
  imageClassName?: string;
  // Desktop only: fit the whole image (object-contain) over a blurred fill,
  // for assets narrower than the very wide desktop banner frame (avoids the
  // heavy top/bottom crop that made them look zoomed in).
  desktopContain?: boolean;
}

const defaultSlides: BannerSlide[] = [
  {
    id: 8,
    type: 'image',
    title: '',
    subtitle: '',
    tagline: '',
    buttonText: '',
    buttonLink: '/create',
    gradient: 'from-black/60 via-black/40 to-transparent',
    image: '/images/banners/banner-1.avif',
    mobileImage: '/images/banners/banner-1-mobile.avif',
    imageAlt: 'Create your own AI Girlfriend',
  },
  {
    id: 7,
    type: 'image',
    title: '',
    subtitle: '',
    tagline: '',
    buttonText: '',
    buttonLink: '/catalog',
    action: 'auth-modal',
    gradient: 'from-black/60 via-black/40 to-transparent',
    image: '/images/banners/banner-2.avif',
    mobileImage: '/images/banners/banner-2-mobile.avif',
    imageAlt: 'Get 100 free roses on sign-up — start your free 3-day trial',
  },
  {
    id: 9,
    type: 'image',
    title: '',
    subtitle: '',
    tagline: '',
    buttonText: '',
    buttonLink: '/catalog',
    gradient: 'from-black/60 via-black/40 to-transparent',
    image: '/images/banners/banner-3.avif',
    mobileImage: '/images/banners/banner-3-mobile.avif',
    imageAlt:
      'Your Personal AI Catalog Girl Awaits — start free intimate chatting',
  },
  {
    id: 4,
    type: 'image',
    title: '',
    subtitle: '',
    tagline: '',
    buttonText: '',
    buttonLink: '/chat',
    gradient: 'from-black/60 via-black/40 to-transparent',
    image: '/images/banners/banner-4.avif',
    mobileImage: '/images/banners/banner-4-mobile.avif',
    imageAlt: 'Every Spark Has a Story — New Story Mode',
  },
  {
    id: 5,
    type: 'image',
    title: '',
    subtitle: '',
    tagline: '',
    buttonText: '',
    buttonLink: '/generate',
    gradient: 'from-black/60 via-black/40 to-transparent',
    // ?v busts the CDN cache — same-URL art swaps were served stale.
    image: '/images/banners/banner-5.avif?v=4',
    mobileImage: '/images/banners/banner-5-mobile.avif',
    imageAlt:
      "Create the scene they won't forget — premium AI images and videos",
  },
  {
    id: 6,
    type: 'image',
    title: '',
    subtitle: '',
    tagline: '',
    buttonText: '',
    buttonLink: '/chat',
    gradient: 'from-black/60 via-black/40 to-transparent',
    image: '/images/banners/banner-6.avif',
    mobileImage: '/images/banners/banner-6-mobile.avif',
    imageAlt:
      'Video call your AI girlfriend now — live voice & video calls in real time',
  },
];

// Banner aspect ratio — mobile-first, switched at the `sm` breakpoint (640px)
// to match the container's other `sm:` styles. Pure CSS so it renders correctly
// on the server and never shifts after hydration.
// The slide keyframes live here too: direction-aware enter/exit pairs, same
// duration/easing as the old framer transition (0.5s, ease [0.4,0,0.2,1]).
const BANNER_CSS = `
.edem-banner-frame { aspect-ratio: 2 / 1; }
@media (min-width: 640px) { .edem-banner-frame { aspect-ratio: 2208 / 464; } }
@keyframes edem-slide-in-from-right { from { transform: translateX(100%); } to { transform: translateX(0); } }
@keyframes edem-slide-in-from-left { from { transform: translateX(-100%); } to { transform: translateX(0); } }
@keyframes edem-slide-out-to-left { from { transform: translateX(0); } to { transform: translateX(-100%); } }
@keyframes edem-slide-out-to-right { from { transform: translateX(0); } to { transform: translateX(100%); } }
.edem-slide-anim { animation-duration: ${ANIMATION_MS}ms; animation-timing-function: cubic-bezier(0.4, 0, 0.2, 1); animation-fill-mode: both; }
`;

// Renders a slide's image. When the slide has a phone-specific `mobileImage`,
// it shows that below `sm` and the wide `image` at/above `sm` (each in its own
// aspect frame, so neither is cropped). Otherwise a single image is used.
function SlideImage({
  slide,
  priority,
}: {
  slide: BannerSlide;
  priority: boolean;
}) {
  const objectClass = slide.imageClassName ?? 'object-cover object-center';
  const alt = slide.imageAlt || slide.title || 'Banner';

  if (slide.mobileImage && slide.image) {
    return (
      <>
        {/* Media-scoped preloads pin the LCP image's discovery to the
            document head (React hoists these): without them the fetch starts
            ~1s after TTFB and the PSI simulator amplifies that lag. Safe with
            the plain <img>/<picture> below — identical bare URLs, so the
            preload is a guaranteed cache hit (the old next/image ?dpl= suffix
            that caused double-fetches is gone). */}
        {priority && (
          <>
            <link
              rel="preload"
              as="image"
              href={slide.mobileImage}
              media="(max-width: 639px)"
              // @ts-expect-error React types lag the fetchpriority attr on link
              fetchpriority="high"
            />
            <link
              rel="preload"
              as="image"
              href={slide.image}
              media="(min-width: 640px)"
              // @ts-expect-error React types lag the fetchpriority attr on link
              fetchpriority="high"
            />
          </>
        )}
        {/* Desktop: blurred cover fill so a contain-fit image has no stark
            side bars (used when the asset is narrower than the wide frame).
            Decorative and CSS-hidden on mobile, so lazy: never fetched on
            phones, loads instantly in-viewport on desktop. */}
        {slide.desktopContain && (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={slide.image}
            alt=""
            aria-hidden
            loading="lazy"
            className="absolute inset-0 hidden h-full w-full scale-110 object-cover blur-2xl brightness-[0.55] sm:block"
          />
        )}
        {/* LCP-critical art direction via <picture>: the browser fetches ONLY
            the variant whose media query matches — eagerly, at high priority.
            Two CSS-hidden <Image priority>s made phones download the desktop
            banner too (~170KB dead inside the LCP window), and the
            lazy+preload workaround tanked the Lighthouse simulator (lazy LCP
            is modeled as gated behind script work → 12s). Plain <img>, not
            next/image: Vercel's ?dpl= suffix and the optimizer add nothing to
            local unoptimized AVIFs. */}
        <picture>
          <source media="(max-width: 639px)" srcSet={slide.mobileImage} />
          <source media="(min-width: 640px)" srcSet={slide.image} />
          {}
          <img
            src={slide.image}
            alt={alt}
            loading={priority ? 'eager' : 'lazy'}
            fetchPriority={priority ? 'high' : undefined}
            className={`absolute inset-0 h-full w-full ${objectClass} ${
              slide.desktopContain ? 'sm:object-contain sm:object-center' : ''
            }`}
          />
        </picture>
      </>
    );
  }

  if (!slide.image) return null;

  return (
    <Image
      src={slide.image}
      alt={alt}
      fill
      className={objectClass}
      priority={priority}
      sizes="100vw"
      unoptimized
    />
  );
}

interface CarouselBannerProps {
  slides?: BannerSlide[];
}

export function CarouselBanner({
  slides = defaultSlides,
}: CarouselBannerProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  // Index of the slide animating OUT (null when idle). Direction decides
  // which enter/exit keyframe pair both layers use.
  const [leaving, setLeaving] = useState<number | null>(null);
  const [direction, setDirection] = useState(1);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const { openAuthModal } = useAuthModal();
  const router = useRouter();
  const { data: user } = useUser();
  const finishTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  const goTo = useCallback(
    (newIndex: number, dir: number) => {
      setLeaving(currentSlide);
      setDirection(dir);
      setCurrentSlide(newIndex);
      setIsTransitioning(true);

      // Timer, not animationend: the event can be missed if the tab is
      // backgrounded mid-transition, which would wedge isTransitioning.
      if (finishTimer.current) clearTimeout(finishTimer.current);
      finishTimer.current = setTimeout(() => {
        setLeaving(null);
        setIsTransitioning(false);
      }, ANIMATION_MS);
    },
    [currentSlide],
  );

  const nextSlide = useCallback(() => {
    if (isTransitioning) return;
    goTo((currentSlide + 1) % slides.length, 1);
  }, [isTransitioning, goTo, currentSlide, slides.length]);

  const prevSlide = useCallback(() => {
    if (isTransitioning) return;
    goTo((currentSlide - 1 + slides.length) % slides.length, -1);
  }, [isTransitioning, goTo, currentSlide, slides.length]);

  const goToSlide = useCallback(
    (newIndex: number) => {
      if (isTransitioning || newIndex === currentSlide) return;
      goTo(newIndex, newIndex > currentSlide ? 1 : -1);
    },
    [isTransitioning, currentSlide, goTo],
  );

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prevSlide();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        nextSlide();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [prevSlide, nextSlide]);

  // Auto-play — advance to the next slide on an interval (no visible progress bar)
  useEffect(() => {
    const slideTimeout = setTimeout(() => {
      nextSlide();
    }, AUTO_PLAY_INTERVAL);

    return () => clearTimeout(slideTimeout);
  }, [currentSlide, nextSlide]);

  useEffect(
    () => () => {
      if (finishTimer.current) clearTimeout(finishTimer.current);
    },
    [],
  );

  // Native swipe: horizontal-dominant moves past the threshold change slide.
  // touch-action: pan-y on the frame keeps vertical scrolling native.
  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    if (t) touchStart.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const start = touchStart.current;
    touchStart.current = null;
    const t = e.changedTouches[0];
    if (!start || !t) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) < SWIPE_DISTANCE_PX || Math.abs(dx) < Math.abs(dy)) {
      return;
    }
    if (dx < 0) nextSlide();
    else prevSlide();
  };

  const renderSlide = (slide: BannerSlide | undefined, priority: boolean) => {
    if (!slide) return null;

    if (slide.type === 'image') {
      // Image-based slide - entire slide is clickable
      return slide.action === 'auth-modal' ? (
        // Auth modal action - use button instead of link
        <div className="relative h-full w-full bg-black">
          <button
            onClick={() =>
              user ? router.push(slide.buttonLink) : openAuthModal('sign-up')
            }
            className="absolute inset-0 z-10 cursor-pointer"
            aria-label={slide.imageAlt || 'Sign up'}
          />
          <SlideImage slide={slide} priority={priority} />
        </div>
      ) : (
        // Regular link action
        <div className="relative h-full w-full bg-black">
          <Link
            href={slide.buttonLink}
            className="absolute inset-0 z-10"
            aria-label={slide.imageAlt || 'Banner link'}
          />
          <SlideImage slide={slide} priority={priority} />
        </div>
      );
    }

    if (slide.type === 'hero') {
      return (
        <div className="absolute inset-0 bg-black">
          {/* Whole-slide click target — 49 visitors/week rage-tapped the
              artwork because only the small CTA button was interactive.
              Sits under the z-10 content so the real CTA still wins. */}
          <Link
            href={slide.buttonLink}
            aria-label={slide.title}
            className="absolute inset-0 z-[5] cursor-pointer"
          />
          <div
            className={`absolute inset-0 bg-gradient-to-br ${slide.gradient}`}
          >
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(236,72,153,0.3),transparent_50%)]" />
              <div className="absolute top-0 right-0 h-full w-1/2 bg-[radial-gradient(circle_at_80%_50%,rgba(168,85,247,0.2),transparent_60%)]" />
            </div>

            <div className="absolute top-0 right-0 h-full w-1/3 bg-gradient-to-l from-black/60 via-transparent to-transparent">
              <div className="from-eros-pink/30 absolute top-1/4 right-10 h-64 w-32 rounded-full bg-gradient-to-b to-transparent blur-2xl" />
            </div>
            <div className="absolute top-1/3 right-1/4 h-full w-1/4 bg-gradient-to-l from-black/40 via-transparent to-transparent">
              <div className="from-eros-purple/20 absolute top-1/4 right-0 h-56 w-28 rounded-full bg-gradient-to-b to-transparent blur-2xl" />
            </div>
          </div>

          <div className="absolute inset-0 z-10 flex items-center px-4 sm:px-8 md:px-16">
            <div className="max-w-3xl">
              <h1 className="mb-2 text-xl leading-tight font-bold text-white sm:mb-4 sm:text-2xl md:text-4xl lg:text-5xl xl:text-7xl">
                {slide.title}
                <br />
                <span className="from-eros-orange via-eros-pink to-eros-purple bg-gradient-to-r bg-clip-text text-transparent">
                  {slide.subtitle}
                </span>
              </h1>
              {slide.tagline && (
                <p className="mb-4 text-sm text-gray-300 sm:mb-8 sm:text-lg md:text-xl lg:text-2xl">
                  {slide.tagline}
                </p>
              )}
              <Link
                href={slide.buttonLink}
                className="from-eros-button-from to-eros-button-to shadow-eros-pink/30 inline-block rounded-xl bg-gradient-to-r px-5 py-2.5 text-sm font-bold text-white shadow-2xl transition-opacity hover:opacity-90 sm:px-8 sm:py-3 sm:text-base md:px-10 md:py-4 md:text-lg"
                tabIndex={0}
              >
                {slide.buttonText}
              </Link>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        onClick={() => router.push(slide.buttonLink || '/catalog')}
        className={`absolute inset-0 bg-gradient-to-r ${slide.gradient} flex cursor-pointer items-center justify-between px-4 sm:px-8 md:px-16`}
      >
        <div className="flex flex-1 items-center gap-4 sm:gap-8">
          <div className="text-white">
            <h2 className="mb-1 text-xl font-bold drop-shadow-lg sm:mb-2 sm:text-3xl md:text-4xl lg:text-6xl">
              {slide.title}
            </h2>
            <p className="text-sm font-semibold opacity-95 sm:text-xl md:text-2xl lg:text-3xl">
              {slide.subtitle}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 sm:gap-6">
          <Link
            href={slide.buttonLink || '#'}
            className="rounded-xl bg-yellow-400 px-4 py-2 text-sm font-bold whitespace-nowrap text-black shadow-xl transition-colors hover:bg-yellow-300 sm:px-6 sm:py-3 sm:text-base md:px-8 md:py-4 md:text-xl"
            tabIndex={0}
          >
            {slide.buttonText}
          </Link>
        </div>
      </div>
    );
  };

  const enterAnim =
    direction > 0 ? 'edem-slide-in-from-right' : 'edem-slide-in-from-left';
  const exitAnim =
    direction > 0 ? 'edem-slide-out-to-left' : 'edem-slide-out-to-right';

  return (
    // Width matches the sections below (max-w-[1400px] + px-3 sm:px-4) so the
    // banner's edges line up with the character grid / stories.
    <div className="mx-auto w-full max-w-[1400px] sm:mt-6 sm:px-4">
      {/* Taller banner on phones (2:1) vs wide on desktop (~4.76:1). Pure CSS
          so the ratio is correct on the first paint — no post-mount JS flip that
          would jump the banner height (and shove the grid down) on refresh. */}
      <style>{BANNER_CSS}</style>
      <div
        className="edem-banner-frame relative overflow-hidden bg-black sm:rounded-3xl"
        role="region"
        aria-label="Carousel banner"
        aria-live="polite"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        style={{ touchAction: 'pan-y' }}
      >
        {/* Outgoing slide (only during a transition) */}
        {leaving !== null && leaving !== currentSlide && (
          <div
            key={`leaving-${leaving}`}
            className="edem-slide-anim absolute top-0 left-0 z-0 h-full w-full"
            style={{ animationName: exitAnim }}
            aria-hidden
          >
            {renderSlide(slides[leaving], false)}
          </div>
        )}

        {/* Current slide. No animation class while idle — slide 0's SSR
            markup is static, so the LCP paint is never animation-gated. */}
        <div
          key={`current-${currentSlide}`}
          className={`absolute top-0 left-0 z-0 h-full w-full ${
            leaving !== null ? 'edem-slide-anim' : ''
          }`}
          style={leaving !== null ? { animationName: enterAnim } : undefined}
        >
          {renderSlide(slides[currentSlide], currentSlide === 0)}
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          disabled={isTransitioning}
          className="absolute top-1/2 left-2 z-10 -translate-y-1/2 rounded-full bg-black/10 p-1 transition-colors hover:bg-black/25 focus:ring-2 focus:ring-white/50 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 sm:left-4 sm:p-2"
          aria-label="Previous slide"
        >
          <ChevronLeft className="h-4 w-4 text-white/80 sm:h-5 sm:w-5" />
        </button>

        <button
          onClick={nextSlide}
          disabled={isTransitioning}
          className="absolute top-1/2 right-2 z-10 -translate-y-1/2 rounded-full bg-black/10 p-1 transition-colors hover:bg-black/25 focus:ring-2 focus:ring-white/50 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 sm:right-4 sm:p-2"
          aria-label="Next slide"
        >
          <ChevronRight className="h-4 w-4 text-white/80 sm:h-5 sm:w-5" />
        </button>

        {/* Dot Indicators */}
        <div className="absolute bottom-2 left-1/2 z-10 flex -translate-x-1/2 gap-1.5 sm:bottom-4 sm:gap-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              disabled={isTransitioning}
              className={`h-2 rounded-full transition-all focus:ring-2 focus:ring-white/50 focus:outline-none disabled:cursor-not-allowed sm:h-3 ${
                index === currentSlide
                  ? 'w-5 bg-white sm:w-8'
                  : 'w-2 bg-white/50 hover:bg-white/75 sm:w-3'
              }`}
              aria-label={`Go to slide ${index + 1}`}
              aria-current={index === currentSlide ? 'true' : 'false'}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
